import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import CafOfferRow from '../../components/campaign/cafOfferRow';
import CafListRow from '../../components/campaign/cafListRow';
import { BeatLoader } from 'react-spinners';
import {
  cafOfferListFetchData,
  uploadDocumentRequest,
  cafOfferListIsLoading,
  clearCafListData,
  cafListFetchData,
  purchaseRebateCafListFetchData,
  uploadSuccess,
} from '../../actions/importCafFileActions';
import { offerDetailsFetchData } from '../../actions/offerActions';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import NoRowExistsRow from '../../components/common/noRowExistsRow';

const ImportCafFilePage = () => {
  const dispatch = useDispatch();
  const { offerId } = useParams();

  // Redux state selectors
  const offering = useSelector((state) => state.offerDetails);
  const cafoffers = useSelector((state) => state.cafOfferList ?? []);
  const cafHistory = useSelector((state) => state.cafHistoryList ?? []);
  const hasErrored = useSelector(
    (state) => state.cafOfferListHasErrored ?? false,
  );
  const isHistoryLoading = useSelector(
    (state) => state.cafHistoryListIsLoading ?? true,
  );
  const isLoading = useSelector((state) => state.cafOfferListIsLoading);
  const fileUploadSuccess = useSelector((state) => state.fileUploadSuccess);
  const fileUploadErrored = useSelector((state) => state.fileUploadErrored);
  const fileIsUploading = useSelector((state) => state.fileIsUploading);
  const data = useSelector((state) => state.data);
  const errorMessage = fileUploadErrored?.message || fileUploadErrored;
  // Local state
  const [rebateName, setRebateName] = useState('');
  const [emptyRowString] = useState('No Data Found');
  const [fileId, setFileId] = useState('');
  const [uploadId, setUploadId] = useState('');
  const [count, setCount] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [startHistIndex, setStartHistIndex] = useState(0);
  const [endHistIndex, setEndHistIndex] = useState(10);
  const [startResIndex, setStartResIndex] = useState(0);
  const [endResIndex, setEndResIndex] = useState(10);
  const [checkedValues, setCheckedValues] = useState(0);
  const [backButtonVal, setBackButtonVal] = useState('');
  const [rebateType, setRebateType] = useState('');
  const [fieldValidator, setFieldValidator] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [fileSuccess, setFileSuccess] = useState(false);
  const [fileVal, setFileVal] = useState('');
  const timerRef = useRef(null);

  const notifyError = (message) => {
    toast.error(message, {
      autoClose: 5000,
      position: 'bottom-left',
      toastId: `error-${Date.now()}`,
    });
  };

  const notifyInfo = (message) => {
    toast.info(message, {
      autoClose: 5000,
      position: 'bottom-left',
      toastId: `info-${Date.now()}`,
      onOpen: () => dispatch(uploadSuccess(false)), // Reset success state
    });
  };

  const notifySuccess = (message) => {
    toast.success(message, {
      autoClose: 5000,
      position: 'bottom-left',
      toastId: `success-${Date.now()}`,
    });
  };

  // Initial data fetch and setup
  useEffect(() => {
    if (!offerId) {
      dispatch(purchaseRebateCafListFetchData());
      setBackButtonVal('/purchase-rebate');
      setRebateType('Purchase');
      setFieldValidator(true);
    } else {
      dispatch(cafListFetchData(offerId));
      dispatch(offerDetailsFetchData(offerId));
      setBackButtonVal('/offers');
      setRebateType('Scan');
      setFieldValidator(false);
    }
    dispatch(clearCafListData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId]);

  // Success message for upload
  useEffect(() => {
    if (fileUploadSuccess) {
      notifyInfo('File uploaded successfully. Now processing file...');
    } else if (fileUploadErrored && errorMessage !== '' && !fileIsUploading) {
      notifyError('File failed upload. Please refresh the page and try again');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileIsUploading]);

  // CAF history notification
  useEffect(() => {
    if (cafHistory && uploadId) {
      const negResult = cafHistory.filter(
        (element) => element.id === uploadId && element.isValid == false,
      );
      const posResult = cafHistory.filter(
        (element) => element.id === uploadId && element.isValid == true,
      );

      if (negResult.length !== 0) {
        notifyError('Your latest submission has errors');
      } else if (posResult.length !== 0) {
        notifySuccess('File has loaded successfully');
      }
    } else {
      toast.dismiss(); // Clear existing toasts
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cafHistory.length, uploadId]);

  // Timer logic for file upload
  useEffect(() => {
    if (fileId && isLoading) {
      clearInterval(timerRef.current);
      toast.dismiss(); // Clear existing toasts

      timerRef.current = setInterval(() => {
        setCount((prev) => prev + 1);
        dispatch(cafOfferListFetchData(fileId));

        if (count > 100 || (cafoffers && cafoffers.length > 0)) {
          clearInterval(timerRef.current);
          dispatch(cafOfferListIsLoading(false));
          setCount(0);
          if (!offerId) {
            dispatch(purchaseRebateCafListFetchData());
          } else {
            dispatch(cafListFetchData(offerId));
          }
        }

        if (count > 100) {
          const errorString =
            'There appears to have been an error loading the file or the process has timed out, please try again.';
          notifyError(errorString);
        }

        if (cafoffers && cafoffers.length > 0) {
          setFileSuccess(true);
        }
      }, 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, fileId, count]);

  // Handlers
  const handleAddNewSubmit = (e) => {
    e.preventDefault();
    const checkboxes = document.querySelectorAll("input[type='checkbox']");
    const checkedOne = Array.prototype.slice
      .call(checkboxes)
      .some((x) => x.checked);

    if (!offerId && !checkedOne) {
      setUploadError(true);
      return;
    } else {
      setUploadError(false);
      const file = e.target.offerFile.files[0];
      const newFileId = uuidv4();
      setFileId(newFileId);
      setUploadId(newFileId);
      const starterDate = startDate
        ? moment(startDate).format('DD-MMM-YYYY')
        : null;
      dispatch(
        uploadDocumentRequest(
          file,
          rebateName,
          file.name,
          offerId || '',
          newFileId,
          starterDate,
          rebateType,
          checkedValues,
        ),
      );
      dispatch(cafOfferListIsLoading(true));
    }
  };

  const handleNameChange = (e) => setRebateName(e.target.value);

  const handleChange = (e) => setFileVal(e.target.value);

  const checkboxChange = () => {
    const checkboxes = document.querySelectorAll(
      "input[type='checkbox']:checked",
    );
    const values = [];
    Array.prototype.forEach.call(checkboxes, (el) =>
      values.push(parseInt(el.value)),
    );
    const finVal = values.reduce((a, b) => a + b, 0);
    setCheckedValues(finVal);
  };

  const dateChange = (date) => setStartDate(date);

  const showResults = (id) => {
    setFileId(id);
    dispatch(cafOfferListIsLoading(true)); // <-- Start polling after row click
  };

  // Pagination handlers
  const nextHistVals = () => {
    setStartHistIndex(startHistIndex + 10);
    setEndHistIndex(endHistIndex + 10);
  };
  const prevHistVals = () => {
    setStartHistIndex(startHistIndex - 10);
    setEndHistIndex(endHistIndex - 10);
  };
  const nextResVals = () => {
    setStartResIndex(startResIndex + 10);
    setEndResIndex(endResIndex + 10);
  };
  const prevResVals = () => {
    setStartResIndex(startResIndex - 10);
    setEndResIndex(endResIndex - 10);
  };

  // UI helpers
  const shownPrev = { display: startResIndex === 0 ? 'none' : 'inline' };
  const showButPrev = { display: startHistIndex === 0 ? 'none' : 'inline' };
  const showNextHistBut = {
    display:
      endHistIndex > (cafHistory ? cafHistory.length : 0) ? 'none' : 'inline',
  };
  const showNextResBut = {
    display:
      endResIndex > (cafoffers ? cafoffers.length : 0) ? 'none' : 'inline',
  };
  const displaySection = { display: !offerId ? 'block' : 'none' };
  const displayEDate = { display: !offerId ? 'none' : 'block' };

  const cafHistoryRows =
    cafHistory && cafHistory.length > 0 ? (
      cafHistory.slice(startHistIndex, endHistIndex).map((cafHistoryItem) => (
        <div className='panel panel-default' key={cafHistoryItem.id}>
          <CafListRow
            showresults={showResults}
            cafhistory={cafHistoryItem}
            newFileId={uploadId}
            notifyError={(msg) => notifyError(msg)}
          />
        </div>
      ))
    ) : !isHistoryLoading ? (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    ) : (
      <></>
    );

  const cafOfferRows =
    cafoffers && cafoffers.length > 0 ? (
      cafoffers.slice(startResIndex, endResIndex).map((cafoffer, index) => (
        <div className='panel panel-default' key={index}>
          <CafOfferRow cafoffer={cafoffer} />
        </div>
      ))
    ) : !isLoading ? (
      <NoRowExistsRow emptyRowText={emptyRowString} />
    ) : (
      <></>
    );

  const cafFileDetail =
    offering && offerId ? (
      <h4>
        Upload a Scan Rebate CAF file for offer {offering.externalId} -{' '}
        {offering.name}
      </h4>
    ) : (
      <h4>Upload a Purchase Rebate CAF File</h4>
    );

  const errorUploadMessage = uploadError ? (
    <Alert className='mb-4' color='danger'>
      Please select at least one rebate type
    </Alert>
  ) : null;

  return (
    <div>
      <Form onSubmit={handleAddNewSubmit}>
        <ToastContainer
          toastClassName='ppcToastContainer'
          position='bottom-left'
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnHover
          theme='light'
        />
        <Container fluid>
          {errorUploadMessage}
          <Card>
            <CardHeader>{cafFileDetail}</CardHeader>
            <CardBody>
              <Row style={displaySection}>
                <Col md='6'>
                  <FormGroup>
                    <Label for='source' className='me-sm-2'>
                      Rebate Name
                    </Label>
                    <Input
                      type='text'
                      id='rebateName'
                      name='rebateName'
                      onChange={handleNameChange}
                      required={fieldValidator}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row style={displaySection}>
                <Col md='6'>
                  <FormGroup className='me-sm-2 mb-sm-0'>
                    <Label for='source' className='me-sm-2'>
                      Rebate Type
                    </Label>
                  </FormGroup>
                  <FormGroup required check className='me-sm-2 mb-sm-0'>
                    <FormGroup>
                      <Label check>
                        <Input
                          type='checkbox'
                          name='rebateType'
                          value='1'
                          onChange={checkboxChange}
                        />{' '}
                        DSD
                      </Label>
                    </FormGroup>
                    <FormGroup>
                      <Label check>
                        <Input
                          type='checkbox'
                          name='rebateType'
                          value='2'
                          onChange={checkboxChange}
                        />{' '}
                        Investment
                      </Label>
                    </FormGroup>
                    <FormGroup>
                      <Label check>
                        <Input
                          type='checkbox'
                          name='rebateType'
                          value='4'
                          onChange={checkboxChange}
                        />{' '}
                        CLP
                      </Label>
                    </FormGroup>
                  </FormGroup>
                </Col>
              </Row>
              <br />
              <Row>
                <Col md='6'>
                  <FormGroup>
                    <Input
                      type='file'
                      id='offerFile'
                      name='offerFile'
                      required
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md='3'>
                  <FormGroup
                    style={displayEDate}
                    className='me-sm-2 mb-sm-0 mt-3 mb-3'
                  >
                    <Label for='startDate' className='me-sm-2'>
                      Effective Date
                    </Label>
                    <DatePicker
                      autoComplete='off'
                      id='startDate'
                      name='startDate'
                      dateFormat='DD/MMM/YYYY'
                      onChange={dateChange}
                      locale='en-au'
                      selected={startDate}
                    />
                  </FormGroup>
                  <br />
                  <FormGroup>
                    <Button disabled={!fileVal} color='primary'>
                      Upload
                    </Button>
                    <Link to={backButtonVal}>
                      <Button className='ms-3'>Back</Button>
                    </Link>
                    {typeof data === 'string' && <div>{data}</div>}
                    {fileIsUploading && <div>Uploading...</div>}
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
          <br />
        </Container>
      </Form>
      <Container fluid>
        <Card>
          <CardHeader>
            <h4>History</h4>
          </CardHeader>
          <CardBody>
            <div className='pageButs mb-3'>
              <Button
                className='pageButtons'
                style={showNextHistBut}
                onClick={nextHistVals}
              >
                Next
              </Button>
              <Button
                className='me-sm-3 pageButtons'
                style={showButPrev}
                onClick={prevHistVals}
              >
                Previous
              </Button>
            </div>
            <br />
            <br />
            <Row>
              <Col md='3' className='column-header white-border'>
                Timestamp
              </Col>
              <Col md='3' className='column-header white-border'>
                Effective Date
              </Col>
              <Col md='3' className='column-header white-border'>
                Uploaded User
              </Col>
              <Col md='3' className='column-header white-border'>
                Status
              </Col>
            </Row>
            {cafHistoryRows}
            <Row>
              <Col>
                <BeatLoader color={'#004C5F'} loading={isHistoryLoading} />
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      <br />
      <Container fluid>
        <BeatLoader color={'#004C5F'} loading={isLoading} />
        <br />
        {!isLoading && cafoffers && cafOfferRows.length > 0 && (
          <Card>
            <CardHeader>
              <h4>Results</h4>
            </CardHeader>
            <CardBody>
              <div className='pageButs mb-3'>
                <Button
                  className='pageButtons'
                  style={showNextResBut}
                  onClick={nextResVals}
                >
                  Next
                </Button>
                <Button
                  className='me-sm-3 pageButtons'
                  style={shownPrev}
                  onClick={prevResVals}
                >
                  Previous
                </Button>
              </div>
              <br />
              <br />
              <Row>
                <Col md='1' className='column-header white-border'>
                  Product Code
                </Col>
                <Col md='4' className='column-header white-border'>
                  Product Name
                </Col>
                <Col md='2' className='column-header white-border'>
                  Cost Level
                </Col>
                <Col md='1' className='column-header white-border'>
                  Offer Funder Id
                </Col>
                <Col md='1' className='column-header white-border'>
                  Rebate Amount
                </Col>
                <Col md='3' className='column-header white-border'>
                  Error Messages
                </Col>
              </Row>
              {cafOfferRows}
              <Row>
                <Col>
                  <BeatLoader color={'#004C5F'} loading={isLoading} />
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default ImportCafFilePage;
